import { queryOptions, useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { apiScoping } from '@/lib/api/scoping'
import { installationPhaseKeys } from '@/features/plant-assessment/api/query-keys'
import type { InstallationPhase } from '@/features/plant-assessment/domain/installation-phase'

const InstallationPhaseSchema = z.object({
  id: z.string(),
  plantAssessmentId: z.string(),
  name: z.string(),
  targetDate: z.string().nullish(),
  hubspotDealId: z.string().nullish(),
  lastSyncedAt: z.string().nullish()
})

const ListInstallationPhasesResponseSchema = z.object({
  installationPhases: z.array(InstallationPhaseSchema)
})

function toInstallationPhase(dto: z.infer<typeof InstallationPhaseSchema>): InstallationPhase {
  return {
    id: dto.id,
    plantAssessmentId: dto.plantAssessmentId,
    name: dto.name,
    targetDate: dto.targetDate ?? undefined,
    hubspotDealId: dto.hubspotDealId ?? undefined,
    lastSyncedAt: dto.lastSyncedAt ?? undefined
  }
}

async function fetchInstallationPhases(plantAssessmentId: string): Promise<InstallationPhase[]> {
  const res = await apiScoping.get('/api/v1/installation-phase', {
    params: { plantAssessmentId }
  })
  const result = ListInstallationPhasesResponseSchema.safeParse(res.data)
  if (!result.success) {
    console.error(
      '[installation-phase-list] Zod parse failed - raw response:',
      res.data,
      'issues:',
      result.error.issues
    )
    throw result.error
  }
  return result.data.installationPhases.map(toInstallationPhase)
}

export function installationPhaseListOptions(plantAssessmentId: string) {
  return queryOptions({
    queryKey: installationPhaseKeys.list(plantAssessmentId),
    queryFn: () => fetchInstallationPhases(plantAssessmentId),
    staleTime: 60_000,
    enabled: Boolean(plantAssessmentId)
  })
}

export function useInstallationPhaseList(plantAssessmentId: string) {
  return useQuery(installationPhaseListOptions(plantAssessmentId))
}
